/**
 * The unlock **notifications** feed (ADR-0124): a bell button in the top-right
 * toolbar with an unread-count bubble, and a dropdown that lists the cosmetics you
 * have unlocked this session. The point is the AFK drip - leave the tab open through
 * a long idle stretch and every cosmetic the server granted while you were away is
 * waiting under the bell when you come back.
 *
 * Like the wardrobe / settings panels this module owns **only** the DOM wiring; the
 * markup and styling live in `index.html`, and it touches no Pixi and no socket - the
 * item **picture** is produced by an injected {@link NotificationsOptions.renderIcon}
 * (the Pixi art path lives in `main.ts`), and the feed itself is derived purely from
 * the collection pushes `main.ts` already forwards. So it unit-tests headlessly in
 * jsdom, exactly like `wardrobe.ts` / `settings.ts`. Rows are built with
 * `createElement`/`textContent` only - the same XSS-safe discipline as the wardrobe
 * list (an item id can never inject markup).
 *
 * **Where notifications come from - a client-side diff of the collection.** The
 * server pushes the player's *full* owned `{item, hue}` collection on a
 * `CollectionMessage`: once at login (the retroactive grant / whole collection) and
 * again every time a fresh unlock grows it (Slice E, ADR-0050). So {@link
 * NotificationsHandle.setCollection} **seeds a silent baseline from the first push**
 * (otherwise every already-owned item would notify on every login/reconnect) and, on
 * each later push, turns any newly-added pair into one notification. A plain
 * reconnect re-pushes the same collection, which equals the baseline, so it
 * correctly produces nothing.
 *
 * **Scope (v1).** Only unlocks that arrive **while the session is open** are caught
 * (the AFK-with-the-tab-open case). Replaying unlocks earned while the client was
 * fully disconnected would need server-side buffering + a new message and is out of
 * scope. Notifications live in memory for the session; persistence across reloads is
 * a possible follow-up.
 */
import { content } from './sim/index';
import { itemLabel, layerLabel } from './wardrobe';
import type { WardrobePair } from './net/protocol';

const { equipment } = content;

/** One unlock notification: the pair, its derived display fields, and read state. */
export interface UnlockNotification {
  /** The unlocked equipment id (e.g. `hair.long`). */
  item: string;
  /** The unlocked dye hue id (0 = undyed). */
  hue: number;
  /** Human item name, e.g. "Long Hair" (from the wardrobe's label helper). */
  name: string;
  /** The render layer the item occupies, e.g. "Hair" (from the equipment registry). */
  layer: string;
  /** Whether the player has seen this notification (opening the dropdown marks all read). */
  read: boolean;
}

/** Options for {@link initNotifications}; all optional so tests can stub. */
export interface NotificationsOptions {
  /**
   * Map a 1-based UO hue id to a CSS colour for the swatch - the same approximate
   * preview the wardrobe swatches use (`main.ts` passes the shared `hueCss`).
   * Defaults to a flat grey so headless tests need no palette.
   */
  hueCss?: (hue: number) => string;
  /**
   * Produce the **picture** of an unlocked `{item, hue}` as a DOM element (`main.ts`
   * wires this to the reused Pixi art path - a small baked-sprite icon). Optional:
   * omitted (tests, or before art loads) the row simply shows no picture.
   */
  renderIcon?: (item: string, hue: number) => HTMLElement | null;
  /**
   * Called whenever the dropdown's open-state flips, so the shared toolbar coordinator
   * (`toolbar.ts`) can enforce one-panel-at-a-time. Optional for tests.
   */
  onOpenChange?: (open: boolean) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the notifications feed through. */
export interface NotificationsHandle {
  /**
   * Feed a full-collection push (from `client.onCollection`). The **first** call
   * seeds the silent baseline (no notifications - the login grant is not "new"); each
   * later call diffs against everything seen so far and appends one notification per
   * newly-added pair, bumping the unread count.
   */
  setCollection(unlocked: readonly WardrobePair[]): void;
  /** Open the dropdown (idempotent); marks every notification read, clearing the bubble. */
  open(): void;
  /** Close the dropdown (idempotent). */
  close(): void;
  /** Toggle the dropdown; opening it marks read (what the bell click does). */
  toggle(): void;
  /** Whether the dropdown is currently open. */
  isOpen(): boolean;
  /** The number of unread notifications (what the bubble shows). */
  unreadCount(): number;
  /** The notifications, newest first (the dropdown's order); exposed for tests. */
  list(): readonly UnlockNotification[];
}

/** Stable `item hue` key for the seen-set / diff. */
function pairKey(pair: WardrobePair): string {
  return `${pair.item} ${pair.hue}`;
}

export function initNotifications(opts: NotificationsOptions = {}): NotificationsHandle {
  const doc = opts.doc ?? document;
  const hueCss = opts.hueCss ?? (() => '#888888');
  const button = doc.getElementById('notifications-button');
  const bubble = doc.getElementById('notifications-bubble');
  const panel = doc.getElementById('notifications-panel');
  const listEl = doc.getElementById('notifications-list');
  const closeBtn = doc.getElementById('notifications-close');

  // Every pair the client has ever known about (baseline + everything since). A pair
  // is "new" - and notifies - only when it is absent here at diff time.
  const seen = new Set<string>();
  // Whether the baseline has been seeded. The first push seeds `seen` silently; only
  // later pushes notify. Without this every already-owned item would notify on login.
  let seeded = false;
  // Notifications newest-first (index 0 is the most recent unlock).
  const notifications: UnlockNotification[] = [];
  let open = false;

  const unreadCount = (): number => notifications.reduce((n, x) => n + (x.read ? 0 : 1), 0);

  /** Reflect the current unread count onto the bubble (hidden at zero). */
  const renderBubble = (): void => {
    if (!bubble) return;
    const count = unreadCount();
    bubble.textContent = String(count);
    bubble.hidden = count === 0;
    // Mirror the count onto the button for assistive tech and hover.
    button?.setAttribute(
      'aria-label',
      count === 0 ? 'Notifications' : `Notifications (${count} unread)`,
    );
  };

  /** Build one dropdown row for a notification: picture, name, layer, hue swatch. */
  const renderRow = (n: UnlockNotification): HTMLElement => {
    const row = doc.createElement('div');
    row.className = 'notif-row';
    if (!n.read) row.classList.add('unread');

    const icon = doc.createElement('span');
    icon.className = 'notif-icon';
    const pic = opts.renderIcon?.(n.item, n.hue) ?? null;
    if (pic) icon.appendChild(pic);
    row.appendChild(icon);

    const meta = doc.createElement('div');
    meta.className = 'notif-meta';

    const name = doc.createElement('span');
    name.className = 'notif-name';
    name.textContent = n.name;
    meta.appendChild(name);

    const sub = doc.createElement('span');
    sub.className = 'notif-sub';
    const layer = doc.createElement('span');
    layer.className = 'notif-layer';
    layer.textContent = n.layer;
    const swatch = doc.createElement('span');
    swatch.className = 'notif-swatch';
    swatch.style.background = hueCss(n.hue);
    swatch.title = `Hue ${n.hue}`;
    swatch.setAttribute('aria-label', `Hue ${n.hue}`);
    // The numeric hue id beside its swatch - a small muted label reusing the sub's
    // font/colour, so the row shows both the colour and the value it was dyed from.
    const hueNum = doc.createElement('span');
    hueNum.className = 'notif-hue';
    hueNum.textContent = String(n.hue);
    sub.append(layer, swatch, hueNum);
    meta.appendChild(sub);

    row.appendChild(meta);
    return row;
  };

  /** Repaint the dropdown list from `notifications` (newest first). */
  const renderList = (): void => {
    if (!listEl) return;
    listEl.replaceChildren();
    if (notifications.length === 0) {
      const empty = doc.createElement('p');
      empty.className = 'notif-empty';
      empty.textContent = 'No unlocks yet — stay online to earn cosmetics.';
      listEl.appendChild(empty);
      return;
    }
    for (const n of notifications) listEl.appendChild(renderRow(n));
  };

  const setOpen = (next: boolean): void => {
    if (next === open) {
      // Re-opening an already-open panel is a no-op, but a *click while open should
      // still be able to clear a freshly-arrived unread* - callers use toggle for that.
      return;
    }
    open = next;
    if (panel) panel.hidden = !open;
    button?.setAttribute('aria-expanded', String(open));
    opts.onOpenChange?.(open);
    if (open) {
      // Opening the dropdown is the "I've seen these" gesture: mark all read so the
      // bubble clears, then paint the list and the (now-empty) bubble.
      for (const n of notifications) n.read = true;
      renderList();
      renderBubble();
    }
  };

  button?.addEventListener('click', (e) => {
    e.stopPropagation();
    setOpen(!open);
  });
  closeBtn?.addEventListener('click', () => setOpen(false));

  // Keep clicks inside the panel from reaching the outside-close handler.
  panel?.addEventListener('click', (e) => e.stopPropagation());
  // A click outside the panel (and not on the bell) closes it; so does Escape. Matches
  // the settings popover / wardrobe / board close behaviour for coherence.
  doc.addEventListener('click', (e) => {
    if (!open) return;
    const target = e.target as Node;
    if (panel && !panel.contains(target) && target !== button) setOpen(false);
  });
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) setOpen(false);
  });

  renderBubble();

  return {
    setCollection(unlocked) {
      if (!seeded) {
        // First push = the login grant / full collection. Seed the baseline silently
        // so already-owned items never notify; a reconnect re-push equals this.
        for (const pair of unlocked) seen.add(pairKey(pair));
        seeded = true;
        return;
      }
      // A later push: every pair not already seen is a fresh unlock -> one
      // notification, newest first. De-dupe within the push via the same `seen` set.
      let added = false;
      for (const pair of unlocked) {
        const key = pairKey(pair);
        if (seen.has(key)) continue;
        seen.add(key);
        const item = pair.item;
        const hue = pair.hue;
        // Layer via the equipment registry (the render slot), labelled like the
        // wardrobe's layer headings; an unknown/unbaked id degrades rather than crashing.
        const layer = equipment.has(item) ? layerLabel(equipment.get(item).layer) : 'Unknown';
        notifications.unshift({
          item,
          hue,
          name: itemLabel(item),
          layer,
          read: false,
        });
        added = true;
      }
      if (!added) return;
      renderBubble();
      if (open) {
        // The dropdown is open, so the player is looking at it: keep the new arrivals
        // marked read (they are being seen) and repaint, leaving the bubble cleared.
        for (const n of notifications) n.read = true;
        renderList();
        renderBubble();
      }
    },
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!open),
    isOpen: () => open,
    unreadCount,
    list: () => notifications,
  };
}
